import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Heart } from "lucide-react";
import { seatStatus } from "./seatStatus";
import { AddToCalendar } from "./AddToCalendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABEL = "block font-body text-[10.5px] md:text-[11px] tracking-[0.3em] uppercase text-muted-warm mb-1";
const INPUT = "input-underline !py-2 w-full";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const EMPTY = { name: "", email: "", programId: "", sessionId: "", message: "", consent: false, website: "" };
const FRAME = { borderColor: "rgba(160,122,40,0.45)", boxShadow: "0 24px 60px -36px rgba(160,122,40,0.55)" };

function SuccessPanel({ result }) {
  return (
    <div data-testid="group-registration-success" className="relative mt-8 md:mt-10 bg-white border px-6 md:px-12 py-10 md:py-12 text-center" style={FRAME}>
      <span aria-hidden="true" className="absolute top-0 left-1/2 -translate-x-1/2 h-[2px] w-20" style={{ background: "var(--accent-gold)" }} />
      <Heart className="mx-auto mb-4 w-5 h-5 text-gold" strokeWidth={1.4} fill="currentColor" fillOpacity={0.25} aria-label="szív" />
      <p className="font-serif-display italic text-[26px] md:text-[30px] leading-tight text-graphite">Szeretettel köszöntelek!</p>
      <div className="mt-5 font-body font-light text-[15.5px] md:text-[16.5px] leading-[1.85] text-graphite max-w-2xl mx-auto space-y-3">
        {result.status === "waitlisted" ? (
          <>
            <p data-testid="group-registration-waitlist-success">Köszönöm az érdeklődésedet és a bizalmadat!</p>
            <p>A választott kiscsoportos alkalom jelenleg betelt, azonban sikeresen felkerültél a várólistára.</p>
            <p>Amennyiben felszabadul egy hely, személyesen felveszem veled a kapcsolatot az általad megadott e-mail-címen, és egyeztetjük a csatlakozás lehetőségét.</p>
            <p>
              Addig is szeretettel köszönöm a nyitottságodat a közös kapcsolódásra!
              <Heart className="inline-block ml-1.5 -mt-1 w-4 h-4 text-gold" strokeWidth={1.4} fill="currentColor" fillOpacity={0.25} aria-label="szív" />
            </p>
          </>
        ) : (
          <>
            <p>A jelentkezésed sikeresen megérkezett, köszönöm a bizalmadat!</p>
            <p>A részvétellel és a fizetéssel kapcsolatos további információkat hamarosan személyesen küldöm el neked az általad megadott e-mail-címre.</p>
            <p>A helyed a részvételi díj beérkezésével válik véglegessé.</p>
          </>
        )}
      </div>
      <p className="mt-6 font-body text-[13px] text-graphite-soft" data-testid="group-registration-success-details">
        {result.program_title} · {result.session_label}
      </p>
      {result.status !== "waitlisted" && <AddToCalendar title={result.program_title} session={result.session} />}
      <div className="mt-7">
        {result.status !== "waitlisted" && <p className="font-body font-light text-[15px] text-graphite">Szeretettel:</p>}
        <p className="mt-1 font-serif-display italic text-gold text-[22px]">Szász-Tóth Enikő</p>
        <p className="font-body text-[13px] text-graphite-soft">Forrás-Kód® kísérő</p>
      </div>
    </div>
  );
}

function SessionSelect({ program, value, onChange }) {
  const sessions = (program?.sessions || []).filter((s) => s.open || s.waitlist_open);
  if (program && sessions.length === 0) {
    return <p data-testid="group-registration-no-session" className="font-body font-light text-[14px] text-graphite-soft py-2">Ehhez a programhoz jelenleg nincs nyitott időpont.</p>;
  }
  return (
    <select id="grp-session" name="sessionId" required value={value} onChange={onChange} disabled={!program} data-testid="group-registration-session" className={`${INPUT} bg-transparent cursor-pointer disabled:cursor-not-allowed disabled:opacity-60`}>
      <option value="" disabled>{program ? "Válassz időpontot" : "Előbb válassz programot"}</option>
      {sessions.map((s) => (
        <option key={s.id} value={s.id}>{seatStatus(s) ? `${s.label} — ${seatStatus(s).text}` : s.label}</option>
      ))}
    </select>
  );
}

export default function GroupRegistrationForm({ selectedProgramId, programs, enabled, onSubmitted }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const options = programs.filter((p) => p.status === "published");
  const program = options.find((p) => p.id === form.programId);
  const chosen = (program?.sessions || []).find((s) => s.id === form.sessionId);
  const isWaitlist = !!chosen && !chosen.open && chosen.waitlist_open;

  useEffect(() => {
    if (selectedProgramId) setForm((f) => ({ ...f, programId: selectedProgramId, sessionId: "" }));
  }, [selectedProgramId]);

  const set = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value, ...(name === "programId" ? { sessionId: "" } : {}) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!enabled || sending) return;
    if (form.name.trim().length < 2) return toast.error("Kérlek, add meg a teljes neved.");
    if (!EMAIL_RE.test(form.email.trim())) return toast.error("Kérlek, érvényes e-mail-címet adj meg.");
    if (!form.programId) return toast.error("Kérlek, válaszd ki a programot.");
    if (!form.sessionId) return toast.error("Kérlek, válaszd ki az időpontot.");
    if (!form.consent) return toast.error("Az adatkezelési tájékoztató elfogadása szükséges.");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/group-registrations`, {
        name: form.name, email: form.email.trim(), program_id: form.programId, session_id: form.sessionId,
        message: form.message, consent: form.consent, website: form.website, waitlist: isWaitlist,
      });
      setResult({ ...data, session: chosen });
      onSubmitted?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "A jelentkezés elküldése nem sikerült. Kérlek, próbáld újra.");
      if (err?.response?.status === 409) onSubmitted?.();
    } finally {
      setSending(false);
    }
  };

  if (result) return <SuccessPanel result={result} />;

  return (
    <form onSubmit={handleSubmit} noValidate data-testid="group-registration-form" className="relative mt-8 md:mt-10 bg-white border px-6 md:px-10 py-8 md:py-9 space-y-6 md:space-y-7" style={FRAME}>
      <span aria-hidden="true" className="absolute top-0 left-1/2 -translate-x-1/2 h-[2px] w-20 !mt-0" style={{ background: "var(--accent-gold)" }} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7 !mt-0">
        <div>
          <label htmlFor="grp-name" className={LABEL}>Teljes név <span className="text-gold">*</span></label>
          <input id="grp-name" name="name" type="text" required value={form.name} onChange={set} autoComplete="name" maxLength={120} data-testid="group-registration-name" className={INPUT} />
        </div>
        <div>
          <label htmlFor="grp-email" className={LABEL}>E-mail-cím <span className="text-gold">*</span></label>
          <input id="grp-email" name="email" type="email" required value={form.email} onChange={set} autoComplete="email" inputMode="email" data-testid="group-registration-email" className={INPUT} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 md:gap-7">
        <div>
          <label htmlFor="grp-program" className={LABEL}>Melyik programra jelentkezel? <span className="text-gold">*</span></label>
          <select id="grp-program" name="programId" required value={form.programId} onChange={set} data-testid="group-registration-program" className={`${INPUT} bg-transparent cursor-pointer`}>
            <option value="" disabled>Válassz programot</option>
            {options.map((p) => <option key={p.id} value={p.id}>{`${p.number} – ${p.title}`}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="grp-session" className={LABEL}>Időpont <span className="text-gold">*</span></label>
          <SessionSelect program={program} value={form.sessionId} onChange={set} />
        </div>
      </div>
      <div>
        <label htmlFor="grp-message" className={LABEL}>Van valami, amit már most szívesen megosztanál?</label>
        <textarea id="grp-message" name="message" rows={2} value={form.message} onChange={set} maxLength={2000} data-testid="group-registration-message" className={`${INPUT} resize-y`} />
      </div>
      <input type="text" name="website" value={form.website} onChange={set} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <div className="pt-1">
        <label className="flex items-start gap-3 cursor-pointer" data-testid="group-registration-consent">
          <input type="checkbox" name="consent" checked={form.consent} onChange={set} required className="mt-[5px] accent-gold flex-shrink-0" />
          <span className="font-body font-light text-[13.5px] md:text-[14px] leading-[1.75] text-graphite">
            Elolvastam és elfogadom az{" "}
            <Link to="/adatkezelesi-tajekoztato" data-testid="group-registration-privacy-link" className="text-gold border-b border-gold hover:opacity-80 transition-opacity duration-300">
              adatkezelési tájékoztatót
            </Link>
            . <span className="text-gold">*</span>
          </span>
        </label>
      </div>
      <div className="text-center">
        <button type="submit" disabled={!enabled || sending} data-testid="group-registration-submit" className="cta-graphite inline-flex items-center justify-center gap-3 px-10 py-4 text-[12px] md:text-[13px] uppercase font-body font-medium tracking-[0.22em] w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed">
          {sending ? "Küldés…" : isWaitlist ? "Várólistára jelentkezem" : "Jelentkezés elküldése"} <span aria-hidden="true">→</span>
        </button>
        {!enabled && (
          <p data-testid="group-registration-locked-note" className="mt-4 font-body font-light text-[13px] text-muted-warm">
            Az online jelentkezés hamarosan megnyílik.
          </p>
        )}
      </div>
    </form>
  );
}
